import { Link, router, usePage } from '@inertiajs/react'
import { GitHubLogoIcon } from '@radix-ui/react-icons'
import { ChevronRight, Settings2 } from 'lucide-react'

import { Logo } from '@/components/logo'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { usePersistedCollapsibleOpen } from '@/hooks/use-persisted-collapsible-open'
import { cn } from '@/lib/utils'
import {
  dashboardPath,
  organizationAnalyticsPath,
  organizationDashboardPath,
  organizationRepositoryPath,
  organizationSettingsPath,
  organizationSettingsPublicProfilePath,
  organizationSubscribePath,
  signOutPath,
} from '@/routes'
import type { OrganizationsOrganizationSummary, OrganizationsRepositorySummary } from '@/types'

type DashboardShellProps = {
  children: React.ReactNode
  organization: OrganizationsOrganizationSummary
  repositories: OrganizationsRepositorySummary[]
  activeRepositoryId?: string | null
  title?: string
  hideTitle?: boolean
}

function NavLink({
  href,
  active,
  children,
}: {
  href: string
  active: boolean
  children: React.ReactNode
}) {
  return (
    <Link
      href={href}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'focus-visible:ring-ring flex min-h-11 items-center rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
        active
          ? 'bg-golden-500/15 text-golden-700 dark:text-golden-400'
          : 'text-muted-foreground hover:bg-accent hover:text-foreground',
      )}
    >
      {children}
    </Link>
  )
}

function NavGroupTrigger({ children }: { children: React.ReactNode }) {
  return (
    <CollapsibleTrigger className="text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring flex min-h-11 w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none">
      {children}
      <ChevronRight className="ml-auto size-4 shrink-0 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 motion-reduce:transition-none" />
    </CollapsibleTrigger>
  )
}

export function DashboardShell({
  children,
  organization,
  repositories,
  activeRepositoryId,
  title,
  hideTitle = false,
}: DashboardShellProps) {
  const { currentUser } = usePage().props
  const { url } = usePage()
  const currentPath = url.split('?')[0]
  const orgId = organization.id

  const overviewPath = organizationDashboardPath(orgId)
  const analyticsPath = organizationAnalyticsPath(orgId)
  const settingsPath = organizationSettingsPath(orgId)
  const publicProfilePath = organizationSettingsPublicProfilePath(orgId)
  const subscribePath = organizationSubscribePath(orgId)
  const onDashboardSurface = currentPath === overviewPath || currentPath === analyticsPath
  const [repositoriesOpen, setRepositoriesOpen] = usePersistedCollapsibleOpen(
    `gitdigest:sidebar:repositories:${orgId}`,
    true,
  )
  const [workspaceOpen, setWorkspaceOpen] = usePersistedCollapsibleOpen(
    `gitdigest:sidebar:workspace:${orgId}`,
    true,
  )

  return (
    <div className="bg-muted text-foreground flex min-h-screen flex-col">
      <a
        href="#main-content"
        className="bg-background text-foreground focus:ring-ring sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-50 focus:rounded-md focus:px-4 focus:py-2 focus:ring-2 focus:ring-offset-2 focus:outline-none"
      >
        Skip to main content
      </a>
      <div className="flex flex-1 flex-col md:flex-row">
        <aside
          aria-label="Workspace"
          className="border-border bg-background flex w-full flex-col border-b md:w-64 md:border-b-0 md:border-r"
        >
          <div className="border-border flex items-center justify-between border-b p-4">
            <Link
              href={overviewPath}
              className="focus-visible:ring-ring rounded-sm hover:opacity-90 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
            >
              <Logo className="text-lg" />
            </Link>
          </div>

          <div className="border-border flex items-center gap-3 border-b p-4">
            {organization.logo ? (
              <img src={organization.logo} alt="" className="h-8 w-8 rounded" />
            ) : (
              <span
                aria-hidden="true"
                className="bg-muted flex h-8 w-8 items-center justify-center rounded text-xs font-semibold"
              >
                {organization.name.slice(0, 2).toUpperCase()}
              </span>
            )}
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold">{organization.name}</p>
              <Link
                href={dashboardPath()}
                className="text-muted-foreground focus-visible:ring-ring inline-flex min-h-11 items-center text-sm hover:underline focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
              >
                Switch workspace
              </Link>
            </div>
          </div>

          <nav aria-label="Primary" className="flex-1 space-y-1 overflow-y-auto p-3">
            <NavLink href={overviewPath} active={onDashboardSurface}>
              Dashboard
            </NavLink>

            {repositories.length > 0 ? (
              <Collapsible
                open={repositoriesOpen}
                onOpenChange={setRepositoriesOpen}
                className="group/collapsible pt-2"
              >
                <NavGroupTrigger>
                  <GitHubLogoIcon className="size-4 shrink-0" aria-hidden="true" />
                  <span className="truncate">Repositories</span>
                </NavGroupTrigger>
                <CollapsibleContent className="mt-1 space-y-0.5 pl-2">
                  {repositories.map((repo) => {
                    const href = organizationRepositoryPath(orgId, repo.owner, repo.name)
                    return (
                      <NavLink key={repo.id} href={href} active={activeRepositoryId === repo.id}>
                        {repo.fullName}
                      </NavLink>
                    )
                  })}
                </CollapsibleContent>
              </Collapsible>
            ) : null}

            <Collapsible
              open={workspaceOpen}
              onOpenChange={setWorkspaceOpen}
              className="group/collapsible pt-2"
            >
              <NavGroupTrigger>
                <Settings2 className="size-4 shrink-0" aria-hidden="true" />
                <span>Workspace</span>
              </NavGroupTrigger>
              <CollapsibleContent className="mt-1 space-y-0.5 pl-2">
                <NavLink href={settingsPath} active={currentPath === settingsPath}>
                  Settings
                </NavLink>
                <NavLink href={publicProfilePath} active={currentPath === publicProfilePath}>
                  Public Profile
                </NavLink>
                <NavLink href={subscribePath} active={currentPath === subscribePath}>
                  Billing
                </NavLink>
              </CollapsibleContent>
            </Collapsible>
          </nav>

          <div className="border-border flex items-center justify-between gap-3 border-t p-4">
            <span className="text-muted-foreground truncate text-sm">{currentUser?.name}</span>
            <Button
              type="button"
              variant="outline"
              className="min-h-11 shrink-0"
              onClick={() => router.delete(signOutPath())}
            >
              Sign out
            </Button>
          </div>
        </aside>

        <main id="main-content" tabIndex={-1} className="flex-1 overflow-y-auto p-4 md:p-8">
          {title && !hideTitle ? (
            <h1 className="font-display mb-8 text-2xl leading-none font-extrabold tracking-tight">
              {title}
            </h1>
          ) : null}
          {children}
        </main>
      </div>
    </div>
  )
}
